import { useEffect, useMemo, useState } from 'react';
import { Banner, Card, Page, Tabs, Text } from '@shopify/polaris';
import { presentSettingsTarget } from '../lib/settingsPresentation';
import { getSyncStatusLabel, getSyncTypeLabel } from '../lib/syncLabels';

function formatDateTime(value?: string | null) {
  if (!value) return '—';
  return new Date(value).toLocaleString('uk-UA');
}

function humanizeCode(value?: string | null) {
  if (!value) return '—';

  return String(value)
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/^./, (char) => char.toUpperCase());
}

function presentLogMessage(value?: string | null) {
  if (!value) return '—';
  if (/Cannot reload onec-worker while a sync is running/u.test(value)) {
    return 'Зараз триває синхронізація. Повторіть дію після завершення.';
  }
  if (/Settings file changed since preview/u.test(value)) {
    return 'Налаштування змінилися після перевірки. Оновіть сторінку і повторіть дію.';
  }
  if (/ECONNREFUSED|ECONNRESET|ETIMEDOUT|fetch failed|Unable to reach/u.test(value)) {
    return 'Один із сервісів тимчасово недоступний.';
  }
  if (/ONEC_|LIQPAY_|SHOPIFY_|PM2|worker|queue|stack|TypeError|SyntaxError/u.test(value)) {
    return 'Потрібна технічна перевірка. Деталі збережені в системному журналі.';
  }
  return value;
}

const AUDIT_ACTION_LABELS: Record<string, string> = {
  login: 'Вхід в адмінку',
  logout: 'Вихід з адмінки',
  settings_preview: 'Перевірка змін налаштувань',
  settings_apply: 'Застосування налаштувань',
  settings_defaults_preview: 'Перевірка стандартних значень',
  settings_defaults_apply: 'Запис стандартних значень',
  settings_reset_preview: 'Перевірка скидання налаштувань',
  settings_reset_apply: 'Скидання налаштувань',
  telegram_access_create: 'Додано доступ Telegram',
  telegram_access_update: 'Оновлено доступ Telegram',
  telegram_access_delete: 'Видалено доступ Telegram',
  telegram_runtime_settings_update: 'Оновлено підключення Telegram',
  telegram_runtime_update: 'Оновлено підключення Telegram',
  telegram_webapp_auth: 'Вхід через Telegram Mini App',
};

const AUDIT_STATUS_LABELS: Record<string, string> = {
  success: 'Успішно',
  failed: 'Помилка',
  error: 'Помилка',
  denied: 'Відхилено',
  unauthorized: 'Відхилено',
};

const ALERT_LABELS: Record<string, string> = {
  onec_worker_unreachable: 'Синхронізація з 1С недоступна',
  pay_web_unreachable: 'Платіжний сервіс недоступний',
  liqpay_sandbox: 'LiqPay працює у тестовому режимі',
  scheduler_disabled: 'Автоматична синхронізація вимкнена',
  sync_stale: 'Синхронізація давно не оновлювалася',
};

function getAuditActionLabel(action?: string | null) {
  return action ? AUDIT_ACTION_LABELS[action] || humanizeCode(action) : '—';
}

function getAuditTargetLabel(target?: string | null) {
  if (!target) return '—';
  const presented = presentSettingsTarget(target);
  return presented === target ? humanizeCode(target) : presented;
}

function getAuditStatusLabel(status?: string | null) {
  return status ? AUDIT_STATUS_LABELS[status] || humanizeCode(status) : '—';
}

function getAlertLabel(alert: string) {
  return ALERT_LABELS[alert] || humanizeCode(alert);
}

export function LogsPage({ api, overview }: any) {
  const [selectedTab, setSelectedTab] = useState(0);
  const [syncLog, setSyncLog] = useState<any>(null);
  const [auditLog, setAuditLog] = useState<any>(null);
  const [error, setError] = useState('');

  const refresh = async () => {
    try {
      const [nextSyncLog, nextAuditLog] = await Promise.all([
        api.getSyncLog(50, true),
        api.getAuditLog(100),
      ]);
      setSyncLog(nextSyncLog);
      setAuditLog(nextAuditLog);
      setError('');
    } catch (nextError) {
      setError(nextError instanceof Error ? nextError.message : 'Не вдалося завантажити журнал');
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  const tabs = useMemo(() => ([
    { id: 'sync-log', content: 'Синхронізація', panelID: 'sync-log-panel' },
    { id: 'audit-log', content: 'Зміни в адмінці', panelID: 'audit-log-panel' },
    { id: 'warnings', content: 'Попередження', panelID: 'warnings-panel' },
  ]), []);

  return (
    <Page title="Журнал" subtitle="Історія синхронізацій, змін в адмінці та активних попереджень">
      <div className="admin-stack admin-logs-page">
        {error ? <Banner tone="critical" title="Журнал недоступний"><p>{error}</p></Banner> : null}
        <Card>
          <Tabs tabs={tabs} selected={selectedTab} onSelect={setSelectedTab} />
        </Card>

        {selectedTab === 0 ? (
          <Card>
            <div className="admin-table-wrap">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Тип</th>
                    <th>Статус</th>
                    <th>Початок</th>
                    <th>Завершення</th>
                    <th>Оброблено</th>
                    <th>Помилки</th>
                    <th>Що сталося</th>
                  </tr>
                </thead>
                <tbody>
                  {(syncLog?.history || []).map((row: any) => {
                    const type = getSyncTypeLabel(row.sync_type);
                    const status = getSyncStatusLabel(row.status);
                    return (
                    <tr key={row.id}>
                      <td>
                        <strong>{type.label}</strong>
                        <div className="admin-table__hint">{type.description}</div>
                      </td>
                      <td title={status.description}>{status.label}</td>
                      <td>{formatDateTime(row.started_at)}</td>
                      <td>{formatDateTime(row.completed_at)}</td>
                      <td>{row.items_processed ?? 0}</td>
                      <td>{row.items_failed ?? 0}</td>
                      <td>{presentLogMessage(row.error_message)}</td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Card>
        ) : null}

        {selectedTab === 1 ? (
          <Card>
            <div className="admin-table-wrap">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Час</th>
                    <th>Користувач</th>
                    <th>Дія</th>
                    <th>Розділ</th>
                    <th>Статус</th>
                    <th>Що сталося</th>
                  </tr>
                </thead>
                <tbody>
                  {(auditLog?.history || []).map((row: any) => (
                    <tr key={row.id}>
                      <td>{formatDateTime(row.createdAt)}</td>
                      <td>{row.actor}</td>
                      <td>{getAuditActionLabel(row.action)}</td>
                      <td>{getAuditTargetLabel(row.target)}</td>
                      <td>{getAuditStatusLabel(row.status)}</td>
                      <td>{presentLogMessage(row.errorMessage)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        ) : null}

        {selectedTab === 2 ? (
          <Card>
            <div className="admin-stack">
              <Text as="h2" variant="headingMd">Поточні системні попередження</Text>
              {(overview?.alerts || []).length === 0 ? (
                <Text as="p" variant="bodyMd">Активних попереджень зараз немає.</Text>
              ) : (
                <ul className="admin-list">
                  {(overview?.alerts || []).map((alert: string) => <li key={alert}>{getAlertLabel(alert)}</li>)}
                </ul>
              )}
            </div>
          </Card>
        ) : null}
      </div>
    </Page>
  );
}
